import * as React from 'react';
import classnames from 'classnames';
import { Currency } from 'core/utils';
import { KeypadRow } from './Keypad.Row';
import { Environment } from 'core/utils';
import { ScreenSize } from './ScreenSize';
import { ScreenUtils } from './controls';

const numpadLayout = [
	'7 8 9',
	'4 5 6',
	'1 2 3',
	'0 {numpaddecimal} {clr}',
	'{arrowdown} - {enter}'
];

type KeypadProps = React.PropsWithChildren<{
	onKeyPressed?: (button: string) => void;
	alwaysVisible?: boolean;
	hideFunctionButtons?: boolean;
	screenSize: ScreenSize;
	isLargeDevice: boolean;
}>;

type KeypadState = {
	isVisible: boolean;
};

// El componente de Keypad se usa desde PosScreen. De momento, no sirve para diálogos,
// aunque se puede usar en PosScreen marcados como 'small', que simulan diálogos
// en las tablets.
//
// En PosScreen existe un método, useKeypad, que indica si se debe mostrar el teclado
// o no al hacer foco en los inputs. En las clases hijas se puede redifinir este método
// si no se quiere usar nunca el Keypad porque se va a usar el teclado del sistema.
//
// Cuando en una pantalla queremos usar el Keypad sólo para algunos controles, por ejemplo
// para introducir una cantidad pero no para introducir unas anotaciones, se pueden marcar
// los controles que no usan el keypad con la clase css disable-keypad.

export default class Keypad extends React.PureComponent<KeypadProps, KeypadState> {

	private lastFocusedInput: Nullable<HTMLInputElement> = null;
	private firstInputEvent = true;
	private mounted = false;
	private observer: MutationObserver;
	private ref = React.createRef<HTMLDivElement>();

	constructor(props: KeypadProps) {
		super(props);
		this.state = { isVisible: false };
		this.observer = new MutationObserver(this.onDOMChanged);
	}

	onDOMChanged: MutationCallback = mutations => {
		if (mutations.flatMap(m => Array.from(m.addedNodes)).length) {
			setupInputs(ScreenUtils.getParentScreen(this.ref.current!));
		}
	};

	handleButtonClicked = (button: string): void => {

		if (this.props.onKeyPressed)
			this.props.onKeyPressed(button);

		if (button === '{arrowdown}') {
			this.stopTrackingInput();
			return;
		}

		if (this.lastFocusedInput !== null) {
			triggerKeyboardEvent(this.lastFocusedInput, button, this.firstInputEvent);
			this.firstInputEvent = false;
		}
	};

	startTrackingInput(input: HTMLInputElement) {
		if (input === this.lastFocusedInput) {
			this.lastFocusedInput.focus();
			return;
		}

		// No entiendo porque hay que ponerlo, pero si lo pongo evito que a veces no aparezca
		// el teclado, por lo menos no consigo reproducirlo.
		setTimeout(() => {
			this.lastFocusedInput?.classList.remove('selected');
			input.classList.add('selected');
			this.lastFocusedInput = input;
			this.firstInputEvent = true;
			this.setState({ isVisible: true }, () => input.scrollIntoView({ behavior: 'smooth', block: 'center' }));
		});

	}

	stopTrackingInput() {
		if (!this.mounted)
			return;

		this.setState({ isVisible: false }, () => {
			this.lastFocusedInput?.classList?.remove?.('selected');
			this.lastFocusedInput = null;
		});
	}

	componentWillUnmount(): void {
		this.observer.disconnect();
		this.mounted = false;
	}

	componentDidMount(): void {

		const keypadNode = this.ref.current! as HTMLElement;

		const screenNode = ScreenUtils.getParentScreen(this.ref.current!);
		this.observer.observe(screenNode, { childList: true, subtree: true });

		setupInputs(screenNode);


		// No me sirve basarme en focusin/focusout porque en safari cuando
		// pulsas fuera de un input no pierde el foco, y no se podría ocultar
		// el teclado. Me baso en click,
		// y si se toca fuera de un input oculto el teclado.
		screenNode.addEventListener('click', (ev: Event) => {
			if (isKeypadEvent(ev, keypadNode))
				return;

			if (shouldHandleKeysFor(ev.target as HTMLElement)) {
				this.startTrackingInput(ev.target as HTMLInputElement);
			} else {
				this.stopTrackingInput();
			}
		});

		// Cubro también el caso en que llega el foco a un input sin hacer click,
		// por ejemplo al crear una vista, al pulsar enter en otro control, o de
		// cualquier forma "programática".
		screenNode.addEventListener('focusin', (ev: Event) => {
			if (isKeypadEvent(ev, keypadNode))
				return;

			if (shouldHandleKeysFor(ev.target as HTMLElement)) {
				this.startTrackingInput(ev.target as HTMLInputElement);
			}
		});

		// A veces parace que hay una carrera crítica entre al activación y desactivación
		// de los focustrap de los Screen, y se queda activa el focustrap del diálogo anterior
		// provocando que al primer focusin del nuevo Screen le hagan un stopImmediatePropagation
		// y no nos enteremos. Fuerzo a hacer el tracking del elemento activo si es un input
		// trackeable.
		setTimeout(() => {
			if (shouldHandleKeysFor(document.activeElement as HTMLElement))
				this.startTrackingInput(document.activeElement as HTMLInputElement);
		});

		this.mounted = true;
	}

	render() {
		const layout = [...numpadLayout];
		const rows = layout.map((r: string, idx: number) => {
			if (idx === layout.length - 1)
				return null;

			const buttons = r.split(' ');
			return <KeypadRow key={`row${idx}`} buttons={buttons} handleButton={this.handleButtonClicked} rowIndex={idx} />;
		});

		const isSmallOrMediumScreenOnLargeDevice = this.props.screenSize !== 'large' && this.props.isLargeDevice;
		const isAlwaysVisible = this.props.alwaysVisible || isSmallOrMediumScreenOnLargeDevice;

		let layoutFunctionButtons = layout[layout.length - 1].split(' ');
		layoutFunctionButtons = isAlwaysVisible
			? layoutFunctionButtons.slice(1) // Si estoy en un diálogo me quito el primer boton de función que es el que oculta el teclado
			: layoutFunctionButtons;

		const functionRow = layoutFunctionButtons.map((r: string, idx: number) => {
			return <KeypadRow key={`rowFn${idx}`} buttons={[r]} handleButton={this.handleButtonClicked} rowIndex={idx} functionRow />;
		});

		const isVisible = isAlwaysVisible || this.state.isVisible;

		const classes = classnames({
			'keypad-container-wrapper': true,
			'keypad-hidden': !isVisible
		});

		return <div ref={this.ref} className={classes}>
			<div id='keypad-container' className='keypad-container'>
				<div className='keypad keypad-numpad'>
					{rows}
				</div>
				{!this.props.hideFunctionButtons && <div className='keypad keypad-numpad-fn'>
					{functionRow}
				</div>}
			</div>
		</div>;
	}
}

function getUpdatedContent(oldContent: string, button: string, append = true): string {
	if (button === '{arrowdown}')
		return oldContent;

	if (button === '{clr}')
		return '';

	if (button === '{numpaddecimal}')
		return append ? oldContent.toString() + Currency.DECIMAL_SEPARATOR : Currency.DECIMAL_SEPARATOR;

	if (button === '-') {
		if (append)
			return oldContent.startsWith('-') ? oldContent.slice(1) : '-' + oldContent.toString();
		return '-';
	}

	return append ? oldContent.toString() + button.toString() : button.toString();
}

function triggerKeyboardEvent(input: HTMLInputElement, button: string, firstInputEvent: boolean) {

	if (button === '{enter}') {
		input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keypress', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: false }));
		return;
	}

	const updatedContent = getUpdatedContent(input.value, button, !firstInputEvent);

	// se supone que esto ya lo mira react el solito para lanzar o no su evento de mentira
	if (updatedContent !== input.value) {

		// Hago primero el setValue en el input destino para evitarme problemas con inputs
		// sincronizados que se "quedan atrás"
		const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')!.set;
		nativeSetter!.call(input, updatedContent);

		input.dispatchEvent(new KeyboardEvent('keydown', { key: button, code: button, bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keypress', { key: button, code: button, bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keyup', { key: button, code: button, bubbles: true, cancelable: false }));
		input.dispatchEvent(new Event('input', { bubbles: true }));
	}
}

function setupInputs(screenNode: HTMLElement) {
	Array.from(screenNode.querySelectorAll('input'))
		.filter(shouldHandleKeysFor)
		.forEach(input => input.readOnly = Environment.isMobile());
}

function isInput(target: EventTarget): target is HTMLInputElement {
	return (target as HTMLInputElement).nodeName === 'INPUT';
}

function shouldHandleKeysFor(target: Nullable<HTMLElement>): boolean {

	if (target === null || !isInput(target))
		return false;

	if (target.disabled)
		return false;

	const inputType = (target as HTMLInputElement).type;
	if (inputType !== 'text' && inputType !== 'number')
		return false;

	const classes = (target as HTMLInputElement).classList;
	if (classes.contains('disable-keypad'))
		return false;

	return true;
}

function isKeypadEvent(ev: Event, keyboardNode: Element) {
	return ev.target === keyboardNode || (ev.target as Element).closest('#keypad-container');
}
